import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Loader2, KeyRound, Lightbulb, ArrowLeft, ArrowRight, DoorOpen, Sparkles, RotateCcw, Clock, Hourglass } from "lucide-react";
import { api } from "@/lib/api";
import { CocoonMark } from "@/components/CocoonMark";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ShareBar } from "@/components/ShareBar";
import { Footer } from "@/components/Footer";

const L = {
  fr: { title: "La Chambre des Énigmes", tagline: "Cinq portes te séparent de la liberté. Chaque serrure est une énigme. Résous, ouvre, avance.", start: "Entrer dans la Chambre", loading: "La Chambre s'assemble…", door: "Porte", of: "sur", yourAnswer: "Ta réponse…", unlock: "Ouvrir la porte", hint: "Un indice", noHint: "Plus d'indice", wrong: "La serrure résiste… réessaie.", opened: "La porte s'ouvre !", answerWas: "Réponse", winTitle: "Tu t'es évadé·e !", winMsg: "Cinq portes, cinq énigmes vaincues.", eclat: "Éclat", hintsUsed: "indices utilisés", replay: "Rejouer", home: "Accueil", timed: "Chrono d'évasion", timedHint: "Un compte à rebours global de 5 min. Le frisson en plus.", time: "Temps", lostTitle: "Le temps t'a rattrapé", lostMsg: "Les portes se sont refermées. Retente ton évasion.", timeBonus: "bonus temps", doorsReached: "portes ouvertes", rules: ["5 portes", "énigmes IA", "3 indices / porte", "réponse libre"], lbTitle: "Le Panthéon des évadés", lbName: "Ton nom d'évadé·e", lbSave: "Graver mon Éclat", lbSaving: "Gravure…", lbRank: "Ton rang", lbAnon: "Anonyme" },
  en: { title: "The Riddle Chamber", tagline: "Five doors stand between you and freedom. Every lock is a riddle. Solve it, open it, move on.", start: "Enter the Chamber", loading: "The Chamber is assembling…", door: "Door", of: "of", yourAnswer: "Your answer…", unlock: "Open the door", hint: "A hint", noHint: "No hint left", wrong: "The lock resists… try again.", opened: "The door opens!", answerWas: "Answer", winTitle: "You escaped!", winMsg: "Five doors, five riddles defeated.", eclat: "Radiance", hintsUsed: "hints used", replay: "Play again", home: "Home", rules: ["5 doors", "AI riddles", "3 hints / door", "free answer"], lbTitle: "Hall of Escapees", lbName: "Your escapee name", lbSave: "Engrave my Radiance", lbSaving: "Engraving…", lbRank: "Your rank", lbAnon: "Anonymous" },
  es: { title: "La Cámara de los Enigmas", tagline: "Cinco puertas te separan de la libertad. Cada cerradura es un enigma. Resuelve, abre, avanza.", start: "Entrar en la Cámara", loading: "La Cámara se ensambla…", door: "Puerta", of: "de", yourAnswer: "Tu respuesta…", unlock: "Abrir la puerta", hint: "Una pista", noHint: "Sin más pistas", wrong: "La cerradura resiste… inténtalo de nuevo.", opened: "¡La puerta se abre!", answerWas: "Respuesta", winTitle: "¡Has escapado!", winMsg: "Cinco puertas, cinco enigmas vencidos.", eclat: "Fulgor", hintsUsed: "pistas usadas", replay: "Jugar de nuevo", home: "Inicio", timed: "Cronómetro de escape", timedHint: "Una cuenta atrás global de 5 min. Emoción extra.", time: "Tiempo", lostTitle: "El tiempo te alcanzó", lostMsg: "Las puertas se cerraron. Intenta escapar de nuevo.", timeBonus: "bono de tiempo", doorsReached: "puertas abiertas", rules: ["5 puertas", "enigmas IA", "3 pistas / puerta", "respuesta libre"], lbTitle: "El Panteón de los fugados", lbName: "Tu nombre de fugado/a", lbSave: "Grabar mi Fulgor", lbSaving: "Grabando…", lbRank: "Tu rango", lbAnon: "Anónimo" },
  nl: { title: "De Raadselkamer", tagline: "Vijf deuren scheiden je van de vrijheid. Elk slot is een raadsel. Los op, open, ga verder.", start: "Betreed de Kamer", loading: "De Kamer wordt opgebouwd…", door: "Deur", of: "van", yourAnswer: "Jouw antwoord…", unlock: "Open de deur", hint: "Een hint", noHint: "Geen hint meer", wrong: "Het slot houdt stand… probeer opnieuw.", opened: "De deur gaat open!", answerWas: "Antwoord", winTitle: "Je bent ontsnapt!", winMsg: "Vijf deuren, vijf raadsels verslagen.", eclat: "Glans", hintsUsed: "hints gebruikt", replay: "Opnieuw spelen", home: "Home", timed: "Ontsnappingstimer", timedHint: "Een globale aftelklok van 5 min. Extra spanning.", time: "Tijd", lostTitle: "De tijd haalde je in", lostMsg: "De deuren vielen dicht. Waag je ontsnapping opnieuw.", timeBonus: "tijdbonus", doorsReached: "deuren geopend", rules: ["5 deuren", "AI-raadsels", "3 hints / deur", "vrij antwoord"], lbTitle: "Pantheon der ontsnapten", lbName: "Jouw ontsnappersnaam", lbSave: "Mijn Glans graveren", lbSaving: "Graveren…", lbRank: "Jouw rang", lbAnon: "Anoniem" },
};

const DIFF = { facile: 152, moyen: 40, difficile: 20, expert: 0 };
const TOTAL_TIME = 300; // 5 min
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function Chambre() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = L[lang] || L.fr;

  const [phase, setPhase] = useState("intro"); // intro | loading | playing | won | lost
  const [session, setSession] = useState(null);
  const [door, setDoor] = useState(1);
  const [totalDoors, setTotalDoors] = useState(5);
  const [riddle, setRiddle] = useState(null);
  const [answer, setAnswer] = useState("");
  const [hints, setHints] = useState([]);
  const [hintsLeft, setHintsLeft] = useState(3);
  const [feedback, setFeedback] = useState(null); // "wrong" | "opening" | null
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [timed, setTimed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [pseudo, setPseudo] = useState("");
  const [savingScore, setSavingScore] = useState(false);
  const [myRank, setMyRank] = useState(null);
  const [board, setBoard] = useState([]);

  const finalScore = result ? result.score + (timed ? timeLeft : 0) : 0;

  const saveScore = async () => {
    if (savingScore || myRank) return;
    setSavingScore(true);
    try {
      const { data } = await api.post("/chambre/score", { session_id: session, pseudo: (pseudo || "").trim(), score: finalScore, timed });
      setMyRank(data.rank);
      setBoard(data.top || []);
    } catch {
      // en cas d'échec (déjà enregistré), on charge quand même le classement
      try { const { data } = await api.get("/chambre/leaderboard", { params: { limit: 10 } }); setBoard(data.top || []); } catch { /* noop */ }
    } finally {
      setSavingScore(false);
    }
  };

  const hue = riddle ? (DIFF[riddle.difficulty] ?? 40) : 40;

  // Compte à rebours global (chrono optionnel).
  useEffect(() => {
    if (phase !== "playing" || !timed) return;
    const id = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(id); setPhase("lost"); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase, timed]);

  const start = async () => {
    setPhase("loading");
    try {
      const { data } = await api.post("/chambre/start", { lang });
      setSession(data.session_id);
      setDoor(data.door); setTotalDoors(data.total_doors);
      setRiddle({ riddle: data.riddle, category: data.category, difficulty: data.difficulty });
      setHints([]); setHintsLeft(3); setAnswer(""); setFeedback(null);
      setTimeLeft(TOTAL_TIME);
      setPhase("playing");
    } catch { setPhase("intro"); }
  };

  const getHint = async () => {
    if (busy || hintsLeft <= 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/chambre/hint", { session_id: session });
      if (data.hint) { setHints((h) => [...h, data.hint]); setHintsLeft(data.hints_left); }
    } catch { /* noop */ } finally { setBusy(false); }
  };

  const submit = async () => {
    if (busy || !answer.trim()) return;
    setBusy(true); setFeedback(null);
    try {
      const { data } = await api.post("/chambre/answer", { session_id: session, answer: answer.trim() });
      if (!data.correct) { setFeedback("wrong"); setBusy(false); return; }
      setFeedback("opening");
      setTimeout(() => {
        if (data.finished) { setResult(data); setPhase("won"); return; }
        setDoor(data.door);
        setRiddle({ riddle: data.riddle, category: data.category, difficulty: data.difficulty });
        setHints([]); setHintsLeft(3); setAnswer(""); setFeedback(null);
        setBusy(false);
      }, 1400);
    } catch { setFeedback("wrong"); setBusy(false); }
  };

  return (
    <div className="relative min-h-[100svh] bg-background text-foreground overflow-x-clip flex flex-col" data-testid="chambre-page">
      <div className="grain-overlay" aria-hidden />
      <div aria-hidden className="fixed inset-0 pointer-events-none z-0"
        style={{ background: `radial-gradient(120% 90% at 50% 6%, hsl(${hue} 88% 48% / 0.18), transparent 55%), radial-gradient(90% 70% at 50% 108%, hsl(${hue} 80% 42% / 0.12), transparent 60%)`, transition: "background 1s ease" }} />

      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/" className="text-sm text-foreground/60 hover:text-primary transition-colors inline-flex items-center gap-1.5"><ArrowLeft size={15} /> {c.home}</Link>
          </div>
        </div>
      </header>

      {phase === "intro" && (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">
          <motion.div aria-hidden initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
            className="mb-8 w-24 h-32 rounded-t-[2rem] border-2 border-primary/40 flex items-center justify-center relative"
            style={{ background: "linear-gradient(180deg, hsl(38 60% 20% / 0.3), transparent)" }}>
            <KeyRound className="text-primary" size={30} />
            <span className="absolute right-3 top-1/2 w-2 h-2 rounded-full bg-primary/70" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.9 }}
            className="font-display text-gradient-orange text-5xl sm:text-6xl lg:text-7xl leading-[1.05] pb-[0.05em] max-w-3xl" data-testid="chambre-title">{c.title}</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.8 }}
            className="mt-5 text-foreground/70 text-lg sm:text-xl max-w-xl">{c.tagline}</motion.p>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
            {c.rules.map((r, i) => <span key={i} className="text-xs sm:text-sm text-foreground/60 border border-primary/25 rounded-full px-3.5 py-1.5 bg-white/[0.03]">{r}</span>)}
          </motion.div>
          <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.48 }}
            onClick={() => setTimed((v) => !v)} data-testid="chambre-timed-toggle" aria-pressed={timed}
            className={`mt-7 inline-flex items-center gap-3 px-5 py-2.5 rounded-full border transition-colors ${timed ? "border-primary bg-primary/12 text-primary" : "border-white/15 text-foreground/60 hover:border-primary/40"}`}>
            <Hourglass size={16} className={timed ? "text-primary" : "text-foreground/50"} />
            <span className="text-sm font-medium">{c.timed}</span>
            <span className={`relative w-9 h-5 rounded-full transition-colors ${timed ? "bg-primary" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background transition-all ${timed ? "left-4" : "left-0.5"}`} />
            </span>
          </motion.button>
          <p className="mt-2 text-xs text-foreground/40 max-w-xs">{c.timedHint}</p>
          <motion.button initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
            onClick={start} data-testid="chambre-start" className="btn-frost-orange mt-9 px-9 py-4 rounded-full font-semibold text-base inline-flex items-center gap-2">
            {c.start} <ArrowRight size={17} />
          </motion.button>
        </main>
      )}

      {phase === "loading" && (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center gap-5 text-foreground/60 px-6" data-testid="chambre-loading">
          <Loader2 className="animate-spin text-primary" size={40} />
          <p className="font-display italic text-xl">{c.loading}</p>
        </main>
      )}

      {phase === "playing" && riddle && (
        <main className="relative z-10 flex-1 flex flex-col items-center px-4 sm:px-6 pt-24 pb-14 w-full">
          {/* Progress doors */}
          <div className="flex items-center gap-2.5 mb-6" data-testid="chambre-progress">
            {Array.from({ length: totalDoors }).map((_, i) => {
              const n = i + 1;
              const done = n < door, active = n === door;
              return (
                <div key={i} className="flex flex-col items-center gap-1">
                  {done ? <DoorOpen size={20} className="text-primary" /> : <KeyRound size={18} className={active ? "text-primary" : "text-foreground/25"} />}
                  <span className={`w-6 h-1 rounded-full ${done ? "bg-primary" : active ? "bg-primary/50" : "bg-white/10"}`} />
                </div>
              );
            })}
          </div>
          <p className="uppercase tracking-[0.25em] text-xs text-foreground/45 mb-4">{c.door} {door} <span className="text-foreground/30">{c.of} {totalDoors}</span></p>
          {timed && (
            <div className={`mb-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border font-display text-lg tabular-nums ${timeLeft <= 30 ? "border-red-400/60 text-red-300 bg-red-400/10" : "border-primary/30 text-primary bg-primary/[0.06]"}`} data-testid="chambre-timer">
              <Clock size={16} /> {mmss(timeLeft)}
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div key={door}
              initial={{ opacity: 0, rotateY: -25, y: 30 }} animate={{ opacity: 1, rotateY: 0, y: 0 }} exit={{ opacity: 0, rotateY: 25 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-2xl glass rounded-[26px] border p-6 sm:p-9"
              style={{ borderColor: `hsl(${hue} 88% 55% / 0.32)`, boxShadow: `0 22px 60px hsl(${hue} 85% 42% / 0.18)` }}
              data-testid="chambre-riddle-card">
              <p className="font-display text-2xl sm:text-3xl leading-snug text-center mb-7" data-testid="chambre-riddle">{riddle.riddle}</p>

              {hints.length > 0 && (
                <div className="space-y-2 mb-6">
                  {hints.map((h, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                      className="flex items-start gap-2 text-sm text-foreground/70 bg-primary/[0.06] border border-primary/20 rounded-xl px-3.5 py-2.5" data-testid={`chambre-hint-${i}`}>
                      <Lightbulb size={15} className="text-primary mt-0.5 flex-shrink-0" /> <span>{h}</span>
                    </motion.div>
                  ))}
                </div>
              )}

              <motion.div animate={feedback === "wrong" ? { x: [0, -10, 10, -8, 8, 0] } : {}} transition={{ duration: 0.4 }}>
                <input
                  data-testid="chambre-answer-input"
                  value={answer} onChange={(e) => { setAnswer(e.target.value); if (feedback === "wrong") setFeedback(null); }}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder={c.yourAnswer} maxLength={80} disabled={busy && feedback === "opening"}
                  className={`w-full text-center text-lg bg-white/[0.04] border rounded-2xl px-4 py-3.5 outline-none transition-colors ${feedback === "wrong" ? "border-red-400/70" : "border-primary/35 focus:border-primary"}`}
                />
              </motion.div>
              {feedback === "wrong" && <p className="mt-2 text-center text-sm text-red-300" data-testid="chambre-wrong">{c.wrong}</p>}

              <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
                <button onClick={submit} disabled={busy || !answer.trim()} data-testid="chambre-submit"
                  className="btn-frost-orange w-full sm:flex-1 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  {busy && feedback !== "wrong" ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} {c.unlock}
                </button>
                <button onClick={getHint} disabled={busy || hintsLeft <= 0} data-testid="chambre-hint-btn"
                  className="w-full sm:w-auto px-5 py-3.5 rounded-full border border-primary/30 text-primary font-medium inline-flex items-center justify-center gap-2 hover:bg-primary/10 transition-colors disabled:opacity-40">
                  <Lightbulb size={16} /> {hintsLeft > 0 ? `${c.hint} (${hintsLeft})` : c.noHint}
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </main>
      )}

      {phase === "won" && result && (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pt-24 pb-16">
          <motion.div initial={{ opacity: 0, scale: 0.85, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ type: "spring", damping: 16 }}
            className="w-full max-w-md text-center glass rounded-[28px] border border-primary/30 p-10" data-testid="chambre-won"
            style={{ boxShadow: "0 24px 80px hsl(38 90% 45% / 0.25)" }}>
            <motion.div animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.12, 1] }} transition={{ duration: 1.6, repeat: Infinity }}>
              <DoorOpen className="mx-auto text-primary mb-5" size={56} />
            </motion.div>
            <h2 className="font-display text-3xl sm:text-4xl text-gradient-orange pb-[0.05em]">{c.winTitle}</h2>
            <p className="text-foreground/65 mt-3">{c.winMsg}</p>
            <div className="mt-6 flex items-center justify-center gap-6">
              <div><p className="font-display text-4xl text-primary inline-flex items-center gap-1.5"><Sparkles size={22} /> {result.score + (timed ? timeLeft : 0)}</p><p className="text-xs uppercase tracking-wider text-foreground/45 mt-1">{c.eclat}</p></div>
              <div className="w-px h-10 bg-white/10" />
              <div><p className="font-display text-4xl text-foreground/85">{result.hints_used}</p><p className="text-xs uppercase tracking-wider text-foreground/45 mt-1">{c.hintsUsed}</p></div>
            </div>
            {timed && <p className="mt-3 text-sm text-primary/80 inline-flex items-center gap-1.5"><Clock size={14} /> +{timeLeft} {c.timeBonus} · {mmss(timeLeft)}</p>}

            {/* Classement — Le Panthéon des évadés */}
            <div className="mt-7 pt-6 border-t border-white/10 text-left" data-testid="chambre-leaderboard">
              {myRank === null ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    maxLength={24}
                    placeholder={c.lbName}
                    data-testid="chambre-pseudo-input"
                    className="flex-1 bg-black/25 border border-white/10 focus:border-primary/40 rounded-full px-4 py-2.5 text-sm text-foreground/90 outline-none transition-colors"
                  />
                  <button onClick={saveScore} disabled={savingScore} data-testid="chambre-save-score"
                    className="btn-frost-orange px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {savingScore ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {savingScore ? c.lbSaving : c.lbSave}
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-center text-sm text-primary mb-4 inline-flex items-center gap-2 w-full justify-center">
                    <Sparkles size={15} /> {c.lbRank} : <span className="font-display text-lg">#{myRank}</span>
                  </p>
                  <p className="uppercase tracking-[0.2em] text-[0.62rem] text-foreground/40 mb-2">{c.lbTitle}</p>
                  <ol className="space-y-1.5">
                    {board.map((e, i) => {
                      const mine = myRank && i === myRank - 1 && e.score === finalScore && (e.pseudo === ((pseudo || "").trim() || c.lbAnon));
                      return (
                        <li key={i} className={`flex items-center justify-between text-sm rounded-lg px-3 py-1.5 ${mine ? "bg-primary/15 text-primary border border-primary/30" : "text-foreground/70"}`}>
                          <span className="flex items-center gap-2 truncate">
                            <span className="font-display w-5 text-foreground/40">{i + 1}</span>
                            <span className="truncate">{e.pseudo || c.lbAnon}</span>
                            {e.timed && <Clock size={11} className="text-primary/50 flex-shrink-0" />}
                          </span>
                          <span className="font-display tabular-nums text-primary/90 flex items-center gap-1"><Sparkles size={12} /> {e.score}</span>
                        </li>
                      );
                    })}
                  </ol>
                </>
              )}
            </div>

            <ShareBar
              className="mt-7 pt-6 border-t border-white/10"
              lang={lang}
              testPrefix="chambre-share"
              filename="cocoon-chambre.png"
              title={c.title}
              subject={`${c.title} — ${finalScore} ${c.eclat}`}
              summary={`${c.winTitle} ${finalScore} ${c.eclat} · ${c.title}\n${window.location.origin}/chambre`}
              payload={{
                lang,
                eyebrow: c.title,
                title_label: c.winTitle,
                topic: `${finalScore} ${c.eclat}`,
                intro: c.winMsg,
                counts: `${result.hints_used} ${c.hintsUsed}${timed ? ` · ${mmss(timeLeft)}` : ""}`,
              }}
            />

            <button onClick={() => { setResult(null); setMyRank(null); setBoard([]); setPseudo(""); setPhase("intro"); }} data-testid="chambre-replay"
              className="btn-frost-orange mt-8 w-full py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2"><RotateCcw size={16} /> {c.replay}</button>
            <Link to="/" className="block mt-3 text-sm text-foreground/50 hover:text-primary transition-colors">{c.home}</Link>
          </motion.div>
        </main>
      )}

      {phase === "lost" && (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pt-24 pb-16">
          <motion.div initial={{ opacity: 0, scale: 0.85, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ type: "spring", damping: 16 }}
            className="w-full max-w-md text-center glass rounded-[28px] border border-red-400/30 p-10" data-testid="chambre-lost"
            style={{ boxShadow: "0 24px 80px hsl(0 80% 45% / 0.2)" }}>
            <motion.div animate={{ rotate: [0, -6, 6, 0] }} transition={{ duration: 1.4, repeat: Infinity }}>
              <Hourglass className="mx-auto text-red-300/80 mb-5" size={52} />
            </motion.div>
            <h2 className="font-display text-3xl sm:text-4xl text-foreground/90 pb-[0.05em]">{c.lostTitle}</h2>
            <p className="text-foreground/60 mt-3">{c.lostMsg}</p>
            <p className="mt-5 font-display text-2xl text-primary">{door - 1} / {totalDoors} <span className="text-sm text-foreground/45 font-sans">{c.doorsReached}</span></p>
            <button onClick={() => setPhase("intro")} data-testid="chambre-lost-replay"
              className="btn-frost-orange mt-8 w-full py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2"><RotateCcw size={16} /> {c.replay}</button>
            <Link to="/" className="block mt-3 text-sm text-foreground/50 hover:text-primary transition-colors">{c.home}</Link>
          </motion.div>
        </main>
      )}

      {/* Flash à l'ouverture d'une porte */}
      <AnimatePresence>
        {feedback === "opening" && (
          <motion.div key="open-flash" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.6, 0] }} transition={{ duration: 1.3 }}
            className="fixed inset-0 z-30 pointer-events-none flex items-center justify-center"
            style={{ background: "radial-gradient(circle at 50% 45%, hsl(45 95% 55% / 0.4), transparent 60%)" }}>
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1.2, opacity: [0, 1, 0] }} transition={{ duration: 1.3 }} className="text-center">
              <DoorOpen size={64} className="mx-auto text-primary mb-3" />
              <p className="font-display text-3xl text-primary">{c.opened}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {phase === "intro" && <Footer />}
    </div>
  );
}
